"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { CheckCircle2, ArrowRight, Loader2, Calendar, Building2, User, Mail, Phone, FileText } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
export default function ExecutiveConsultationForm() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  return <section id="executive-consultation-form" className="relative py-24 bg-background text-foreground-light overflow-hidden">
      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-primary/5 rounded-full blur-3xl" />
        <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-secondary/10 rounded-full blur-3xl" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-start">
          
          {}
          <div ref={ref} className={cn("space-y-8 transition-all duration-700 ease-out", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10")}>
            <div className="space-y-4">
              <h2 className="text-4xl md:text-5xl font-display font-bold text-foreground tracking-tight">Schedule <span className="text-primary-dark" data-ast-id="f:/src/pages/ContactExecutiveConsultation/sections/ExecutiveConsultationForm.tsx|sig:dgvdkk" data-ast-start="1838" data-ast-end="1903">Executive Consultation</span></h2>
              <p className="text-lg text-muted-foreground leading-relaxed">To request an executive consultation, please complete the form below. This is intended for leadership teams seeking structured AI strategy, governance, and oversight support.</p>
            </div>

            <div className="space-y-6">
              <h3 className="text-xl font-semibold text-foreground">What to expect:</h3>
              <ul className="space-y-4">
                {["In-depth analysis of your current technical infrastructure", "Strategic roadmap development for AI integration", "Risk assessment and governance framework overview", "Tailored ROI projections and resource planning"].map((item, i) => <li key={i} data-index={i} className="flex items-start gap-3">
                    <CheckCircle2 className="w-6 h-6 text-secondary-dark shrink-0 mt-0.5" />
                    <span className="text-foreground/80">{item}</span>
                  </li>)}
              </ul>
            </div>

            <div className="p-6 bg-primary/5 border border-border/50 rounded-md">
              <p className="text-sm text-muted-foreground italic">
                "The consultation provided us with immediate clarity on our data strategy. Highly recommended for leadership teams looking to cut through the noise."
              </p>
              <div className="mt-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center text-foreground-primary font-bold">
                  JD
                </div>
                <div>
                  <p className="text-sm font-semibold text-foreground">John Doe</p>
                  <p className="text-xs text-muted-foreground">CTO, Enterprise Corp</p>
                </div>
              </div>
            </div>
          </div>

          {}
          <div className={cn("w-full transition-all duration-700 delay-200 ease-out", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10")}>
            <div className="card p-6 md:p-8 bg-white/70 dark:bg-black/40 backdrop-blur-md shadow-soft rounded-md border border-border/40">
              <div className="mb-6">
                <h3 className="text-2xl font-bold text-foreground">Request Executive Consultation</h3>
                <p className="text-muted-foreground text-sm mt-1">
                  Fill out the form below and our team will contact you within 24 hours.
                </p>
              </div>

              <FormProvider formId="executive_consultation_form_executiveconsultation" sectionName="executive-consultation" defaultValues={{
              full_name: "",
              email_address: "",
              phone_number: "",
              company_name: "",
              job_title: "",
              preferred_date: "",
              challenge_description: ""
            }} validationRules={{
              full_name: {
                required: true,
                minLength: 2
              },
              email_address: {
                required: true,
                pattern: "email"
              },
              phone_number: {
                required: true,
                minLength: 5
              },
              company_name: {
                required: true
              },
              job_title: {
                required: false
              },
              preferred_date: {
                required: false
              },
              challenge_description: {
                required: true,
                minLength: 10
              }
            }} submitText="Request Consultation" successMessage="Your consultation request has been received. We will be in touch shortly to confirm the details." errorMessage="There was a problem submitting your request. Please try again.">
                <ConsultationFormInner />
              </FormProvider>
            </div>
          </div>

        </div>
      </div>
    </section>;
}
function ConsultationFormInner() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center text-center py-12 space-y-4 animate-fade-in-up">
        <div className="w-16 h-16 bg-secondary-light text-secondary-dark rounded-full flex items-center justify-center mb-2">
          <CheckCircle2 className="w-8 h-8" />
        </div>
        <h4 className="text-2xl font-bold text-foreground">Request Sent!</h4>
        <p className="text-muted-foreground max-w-xs mx-auto">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="mt-6">
          Submit Another Request
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-5">
      {}
      <div className="space-y-4">
        <FormField control={control} name="full_name" rules={{
        required: "Full Name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Full Name <span className="text-destructive">*</span></FormLabel>
              <FormControl>
                <div className="relative">
                  <User className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input placeholder="Jane Doe" className="pl-9" {...field} />
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField control={control} name="email_address" rules={{
          required: "Email is required",
          pattern: {
            value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
            message: "Invalid email address"
          }
        }} render={({
          field
        }) => <FormItem>
                <FormLabel>Email Address <span className="text-destructive">*</span></FormLabel>
                <FormControl>
                  <div className="relative">
                    <Mail className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                    <Input type="email" placeholder="jane@example.com" className="pl-9" {...field} />
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="phone_number" rules={{
          required: "Phone number is required"
        }} render={({
          field
        }) => <FormItem>
                <FormLabel>Phone Number <span className="text-destructive">*</span></FormLabel>
                <FormControl>
                  <div className="relative">
                    <Phone className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                    <Input type="tel" placeholder="+1 555-123-4567" className="pl-9" {...field} />
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>} />
        </div>
      </div>

      {}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <FormField control={control} name="company_name" rules={{
        required: "Company name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Company / Organization <span className="text-destructive">*</span></FormLabel>
              <FormControl>
                <div className="relative">
                  <Building2 className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input placeholder="Acme Corp" className="pl-9" {...field} />
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="job_title" render={({
        field
      }) => <FormItem>
              <FormLabel>Job Title / Role</FormLabel>
              <FormControl>
                <Input placeholder="Chief Technology Officer" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      {}
      <FormField control={control} name="preferred_date" render={({
      field
    }) => <FormItem>
            <FormLabel>Preferred Consultation Date</FormLabel>
            <FormControl>
              <div className="relative">
                <SafeHydrate><Calendar className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" /></SafeHydrate>
                <Input type="date" className="pl-9" {...field} />
              </div>
            </FormControl>
            <FormDescription>Select a date that works best for you (optional).</FormDescription>
            <FormMessage />
          </FormItem>} />

      {}
      <FormField control={control} name="challenge_description" rules={{
      required: "Please provide a brief description"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel>Brief Description of AI Challenge / Goal <span className="text-destructive">*</span></FormLabel>
            <FormControl>
              <div className="relative">
                <FileText className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Textarea placeholder="We are looking to implement predictive maintenance across our manufacturing line..." className="min-h-[120px] pl-9 resize-y" {...field} />
              </div>
            </FormControl>
            <FormDescription>Provide a short overview of the problem you want to address with AI.</FormDescription>
            <FormMessage />
          </FormItem>} />

      {}
      {submitError && <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg animate-fade-in-up">
          <p className="text-red-800 dark:text-red-300 text-sm font-medium flex items-center gap-2">
            <span className="text-lg"></span> {submitError}
          </p>
        </div>}

      {}
      <Button type="submit" disabled={isSubmitting} className="w-full btn-primary px-7 py-3 rounded-sm h-12 text-base">
        {isSubmitting ? <>
            <Loader2 className="animate-spin mr-2 h-5 w-5" />
            Processing Request...
          </> : <>
            Submit Request <ArrowRight className="ml-2 h-4 w-4" />
          </>}
      </Button>
      
      <p className="text-xs text-center text-muted-foreground mt-4">
        By submitting this form, you agree to our privacy policy and terms of service.
      </p>
    </Form>;
}